<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { api } from '@/api/client.js';
import Sparkline from '@/components/charts/Sparkline.vue';
import Stat from '@/components/ds/Stat.vue';
import PanelCard from '@/components/panels/PanelCard.vue';
import { usePanelState } from '@/composables/usePanelState.js';
import { usePoll } from '@/composables/usePoll.js';
import { useTenantStore } from '@/stores/tenant.js';
import { SUMMARY_EMPTY } from '@/utils/emptyStateCopy.js';
import { formatExactPeriod } from '@/utils/timeWindows.js';

// KPI strip (full-width): sessions, pageviews, identified users,
// avg. session, pages/session, bounce rate — con delta vs período
// anterior. Los deltas se calculan aquí (el endpoint entrega ambos
// períodos crudos, mismo contrato que pageviews current/prior).

const tenant = useTenantStore();
const { activeAppId, timeWindow } = storeToRefs(tenant);
const ps = usePanelState('loading');

const EMPTY_PERIOD = Object.freeze({
  sessions: 0,
  pageviews: 0,
  identifiedUsers: 0,
  identifiedSessions: 0,
  avgSessionSec: null,
  pagesPerSession: null,
  bounceRate: null,
});

const data = ref({ current: EMPTY_PERIOD, prior: EMPTY_PERIOD, identifiedTrend: [], periods: null });

async function fetchFn({ signal }) {
  if (!activeAppId.value) return;
  return api.events.summary({ appId: activeAppId.value, window: timeWindow.value, signal });
}

const { tick, status: refreshStatus, lastUpdatedAt } = usePoll(fetchFn, {
  watch: [() => tenant.changeKey],
  onSuccess(result) {
    if (!result || result.ok !== true) return;
    data.value = {
      current: { ...EMPTY_PERIOD, ...(result.current ?? {}) },
      prior: { ...EMPTY_PERIOD, ...(result.prior ?? {}) },
      identifiedTrend: result.identifiedTrend ?? [],
      periods: result.periods ?? null,
    };
    ps.setState('data');
  },
  onError(err) {
    ps.setState('error', {
      message: "Couldn't load KPIs",
      detail: err?.message ?? '',
    });
  },
});

ps.setRetryHandler(() => tick());

// Exponer ps para los panelRefs del LandingView (error global / retry all).
defineExpose({ ps, refreshStatus });

// Delta relativo vs período anterior: "+12%" / "−8%". Sin prior
// comparable (0/null) no hay delta — el Stat lo omite.
function comparisonFor(cur, prior, formatPrior, { percentagePoints = false } = {}) {
  if (cur == null || prior == null) return { text: 'Not enough data', direction: 'flat' };
  if (prior === 0) return cur === 0
    ? { text: 'No change', direction: 'flat' }
    : { text: 'No previous activity', direction: 'up' };
  if (cur === prior) return { text: 'No change', direction: 'flat' };
  if (percentagePoints) {
    const points = Math.abs((cur - prior) * 100).toFixed(1).replace(/\.0$/, '');
    const direction = cur > prior ? 'up' : 'down';
    return { text: `${direction === 'up' ? 'Up' : 'Down'} ${points} pp vs ${formatPrior(prior)} prior`, direction };
  }
  const pct = ((cur - prior) / prior) * 100;
  if (!Number.isFinite(pct)) return { text: 'Not enough data', direction: 'flat' };
  const rounded = Math.abs(pct) < 10 ? Math.abs(pct).toFixed(1) : String(Math.round(Math.abs(pct)));
  const direction = pct > 0 ? 'up' : 'down';
  return { text: `${direction === 'up' ? 'Up' : 'Down'} ${rounded}% vs ${formatPrior(prior)} prior`, direction };
}

function fmtInt(n) {
  return (n ?? 0).toLocaleString('en-US');
}

function fmtDuration(sec) {
  if (sec == null) return '—';
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}

function fmtRatio(v) {
  return v == null ? '—' : v.toFixed(1);
}

function fmtPct(v) {
  return v == null ? '—' : `${Math.round(v * 100)}%`;
}

const kpis = computed(() => {
  const c = data.value.current;
  const p = data.value.prior;
  const comparison = (key, formatter, options) => comparisonFor(c[key], p[key], formatter, options);
  return [
    {
      key: 'sessions',
      label: 'Sessions',
      helpKey: 'sessions',
      value: fmtInt(c.sessions),
      comparison: comparison('sessions', fmtInt),
      rawCurrent: fmtInt(c.sessions),
      rawPrior: fmtInt(p.sessions),
    },
    {
      key: 'pageviews',
      label: 'Pageviews',
      helpKey: 'pageviews',
      value: fmtInt(c.pageviews),
      comparison: comparison('pageviews', fmtInt),
      rawCurrent: fmtInt(c.pageviews),
      rawPrior: fmtInt(p.pageviews),
    },
    {
      key: 'identifiedUsers',
      label: 'Identified users',
      helpKey: 'identifiedUsers',
      value: fmtInt(c.identifiedUsers),
      comparison: comparison('identifiedUsers', fmtInt),
      rawCurrent: fmtInt(c.identifiedUsers),
      rawPrior: fmtInt(p.identifiedUsers),
    },
    {
      key: 'avgSession',
      label: 'Avg. session',
      helpKey: 'avgSession',
      value: fmtDuration(c.avgSessionSec),
      comparison: comparison('avgSessionSec', fmtDuration),
      rawCurrent: fmtDuration(c.avgSessionSec),
      rawPrior: fmtDuration(p.avgSessionSec),
    },
    {
      key: 'pagesPerSession',
      label: 'Pages / session',
      helpKey: 'pagesPerSession',
      value: fmtRatio(c.pagesPerSession),
      comparison: comparison('pagesPerSession', fmtRatio),
      rawCurrent: fmtRatio(c.pagesPerSession),
      rawPrior: fmtRatio(p.pagesPerSession),
    },
    {
      key: 'bounceRate',
      label: 'Bounce rate',
      helpKey: 'bounceRate',
      value: fmtPct(c.bounceRate),
      comparison: comparison('bounceRate', fmtPct, { percentagePoints: true }),
      rawCurrent: fmtPct(c.bounceRate),
      rawPrior: fmtPct(p.bounceRate),
    },
  ];
});

const trendHasData = computed(() =>
  data.value.identifiedTrend.some((b) => b.value > 0));
const periodSummary = computed(() => data.value.periods
  ? `Current: ${formatExactPeriod(data.value.periods.current)}. Prior: ${formatExactPeriod(data.value.periods.prior)}. ${data.value.periods.timezone ?? ''}`
  : '');
</script>

<template>
  <PanelCard
    title="Overview"
    help-key="overview"
    :state="ps.state.value"
    :empty-headline="SUMMARY_EMPTY.headline"
    :empty-body="SUMMARY_EMPTY.body"
    :empty-action="SUMMARY_EMPTY.action"
    :error-message="ps.errorMessage.value"
    :error-detail="ps.errorDetail.value"
    :on-retry="ps.retry"
    :refresh-status="refreshStatus"
    :last-updated-at="lastUpdatedAt"
  >
    <p
      v-if="periodSummary"
      class="kpis__periods"
    >
      {{ periodSummary }}
    </p>
    <div class="kpis">
      <div
        v-for="kpi in kpis"
        :key="kpi.key"
        class="kpis__item"
      >
        <Stat
          :label="kpi.label"
          :help-key="kpi.helpKey"
          :value="kpi.value"
          :delta="kpi.comparison.text"
          delta-tone="neutral"
          :delta-direction="kpi.comparison.direction"
          size="medium"
        />
        <Sparkline
          v-if="kpi.key === 'identifiedUsers' && trendHasData"
          :points="data.identifiedTrend"
          :width="120"
          :height="24"
          aria-label="Identified users trend in this window"
          class="kpis__trend"
        />
      </div>
    </div>
    <template
      v-if="data.current.sessions > 0"
      #tableFallback
    >
      <table class="kpis-table">
        <caption class="visually-hidden">
          Overview KPIs — current vs prior period
        </caption>
        <thead>
          <tr>
            <th scope="col">
              Metric
            </th>
            <th
              scope="col"
              class="numeric"
            >
              Current
            </th>
            <th
              scope="col"
              class="numeric"
            >
              Prior
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="kpi in kpis"
            :key="`t-${kpi.key}`"
          >
            <td>{{ kpi.label }}</td>
            <td class="numeric">
              {{ kpi.rawCurrent }}
            </td>
            <td class="numeric">
              {{ kpi.rawPrior }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </PanelCard>
</template>

<style scoped>
.kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-3);
}

.kpis__periods {
  margin: 0 0 var(--space-3);
  font-size: var(--text-xs);
  color: var(--fg-secondary);
}

@media (width >= 1440px) {
  .kpis {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
}

@media (width <= 639px) {
  .kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (width <= 479px) {
  .kpis {
    grid-template-columns: 1fr;
  }
}

.kpis__item {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
  padding: var(--space-4);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-xl);
  background: var(--bg-canvas);
  box-shadow: var(--shadow-xs);
  overflow-wrap: anywhere;
}

.kpis__item :deep(.ds-stat__value-row) {
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-3);
  margin-top: var(--space-2);
}

.kpis__item :deep(.ds-stat__delta) {
  padding: 0;
  background: transparent;
}

.kpis__item :deep(.ds-stat__delta svg) {
  flex-shrink: 0;
}

.kpis__trend {
  color: var(--chart-1);
}

.kpis-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-xs);
}

.kpis-table th,
.kpis-table td {
  padding: var(--space-1) var(--space-2);
  text-align: left;
  border-bottom: var(--bw-thin) solid var(--border-default);
}

.kpis-table th {
  color: var(--fg-secondary);
  font-weight: var(--fw-medium);
}

.kpis-table .numeric {
  text-align: right;
  font-feature-settings: "tnum";
}
</style>
